import { createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '../../shared/apollo';

import {
  FETCH_MANUAL_HOT_LIST_TYPE,
  FETCH_MANUAL_HOT_LIST_ERROR_TYPE,
  ADD_MANUAL_HOT_LIST_TYPE,
  REMOVE_MANUAL_HOT_LIST_TYPE,
  FETCH_VEHICLE_LIST_TYPE,
  UPDATE_MANUAL_HOT_LIST_TYPE,
} from './manual-hot-cases-constants';
import {
  CREATE_MANUAL_HOT_LIST,
  CaseType,
  FETCH_MANUAL_HOT_LIST,
  ManualHotCase,
  REMOVE_MANUAL_HOT_LIST,
  UPDATE_MANUAL_HOT_LIST,
} from '@rapid-recovery-agency-inc/insightt-shared';
import { InsighttNotification } from '../../helpers/insightt-notification';
import { shiftAdminService } from '../../services/insightt-services';
import { FetchPolicy } from '@apollo/client';
import { ManualHotCasesDocument } from '@rapid-recovery-agency-inc/insightt-graphql';

export const fetchManualHotListAction = createAsyncThunk<
  { manualHotCases: ManualHotCase[] },
  { userId: number; fetchPolicy?: FetchPolicy }
>(FETCH_MANUAL_HOT_LIST_TYPE, async (action, thunkApi) => {
  try {
    const response = await client.query({
      fetchPolicy: action.fetchPolicy || 'network-only',
      query: FETCH_MANUAL_HOT_LIST,
      variables: {
        where: {
          userId: {
            equals: action.userId,
          },
        },
      },
    });
    return thunkApi.fulfillWithValue(response.data);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (err) {
    return thunkApi.rejectWithValue(FETCH_MANUAL_HOT_LIST_ERROR_TYPE);
  }
});

export const fetchHLVehiclesByVIN = createAsyncThunk<{ hotListVehicles: CaseType[] }, { vin: string }>(
  FETCH_VEHICLE_LIST_TYPE,
  async (action, thunkApi) => {
    try {
      const response = await shiftAdminService.getHotListVehicleByVin(action.vin);
      if (response.ok) return thunkApi.fulfillWithValue({ hotListVehicles: response?.data });
      else {
        InsighttNotification('error', response?.data?.message || 'Unable to fetch hot list vehicles');
        return thunkApi.rejectWithValue(response?.data?.message);
      }
    } catch (err) {
      return thunkApi.rejectWithValue(err);
    }
  },
);

export const addVehicleToManualHotListAction = createAsyncThunk<
  { createOneManualHotCase: ManualHotCase },
  Record<string, string>
>(ADD_MANUAL_HOT_LIST_TYPE, async (action, thunkApi) => {
  const { caseId, lenderClientName, year, make, model, note, userId, vin } = action;

  try {
    const response = await client.mutate({
      refetchQueries: [ManualHotCasesDocument],
      mutation: CREATE_MANUAL_HOT_LIST,
      variables: {
        data: {
          vin: vin,
          lenderClientName,
          year,
          make,
          model,
          case:
            caseId && caseId !== 'undefined'
              ? {
                  connect: {
                    caseId: caseId,
                  },
                }
              : {},
          notes: {
            create: [
              {
                text: note,
              },
            ],
          },
          user: {
            connect: {
              id: userId,
            },
          },
        },
      },
    });
    InsighttNotification('success', 'Vehicle was added to manual hot list successfully.');
    thunkApi.dispatch(fetchManualHotListAction({ userId: Number(userId) }));
    return thunkApi.fulfillWithValue(response.data);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    InsighttNotification('error', 'Unable to add a vehicle to manual hot list.');
    return thunkApi.rejectWithValue(ADD_MANUAL_HOT_LIST_TYPE);
  }
});

export const removeManualHotListAction = createAsyncThunk<{ id: string }, { id: string }>(
  REMOVE_MANUAL_HOT_LIST_TYPE,
  async (action, thunkApi) => {
    const { id } = action;

    try {
      const response = await client.mutate({
        mutation: REMOVE_MANUAL_HOT_LIST,
        variables: {
          where: {
            id,
          },
        },
      });
      InsighttNotification('success', 'Vehicle was removed from Manual Hot List successfully.');
      return thunkApi.fulfillWithValue({ ...response.data, id });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      InsighttNotification('error', 'Unable to delete manual hot list.');
      return thunkApi.rejectWithValue(ADD_MANUAL_HOT_LIST_TYPE);
    }
  },
);

export const updateManualHotCaseAction = createAsyncThunk<
  { updateOneManualHotCase: ManualHotCase },
  Record<string, string>
>(UPDATE_MANUAL_HOT_LIST_TYPE, async (action, thunkApi) => {
  const { data, id, successMessage } = action;

  try {
    const response = await client.mutate({
      mutation: UPDATE_MANUAL_HOT_LIST,
      variables: {
        data,
        where: {
          id,
        },
      },
    });
    InsighttNotification('success', successMessage);
    return thunkApi.fulfillWithValue(response.data);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    InsighttNotification('error', 'Unable to modify manual hot case');
    return thunkApi.rejectWithValue(UPDATE_MANUAL_HOT_LIST_TYPE);
  }
});
